import InfoChit from '@/components/chit/InfoChit';
import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import DaaSTable, { DaasTableColumn } from '@/components/table/DaasTable';
import { SortDirection, sort } from '@/components/table/sortHelper';
import { StockLevel } from '@/services/api/contracts/stock/stockLevels';
import { StockBinLevel } from '@/services/catalogue/stockLevels/useDefaultBundleStockLevels';
import { StockLevelStatus, useStockLevelStatus } from '@/services/catalogue/useCustomerCatalogueService';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { StockLevelRow } from './StockLevelRow';
import { StockTableData } from './stockLevel.types';

const StockTableFields = z.object({
  stockData: z.array(StockTableData)
});

type StockTableFields = z.infer<typeof StockTableFields>;

interface Props {
  stockLevels: StockBinLevel[];
  onSaveCallback?: (data: StockTableData[]) => Promise<void>;
  resetStockLevels?: StockBinLevel[];
}

export const StockLevelsTable: FunctionComponent<Props> = ({ stockLevels, onSaveCallback, resetStockLevels }) => {
  const readOnlyTable = !onSaveCallback;

  const tableHeaders: DaasTableColumn[] = [
    {
      accessor: 'deviceName',
      sortable: true,
      text: 'Device Name',
      size: '1fr'
    },
    {
      accessor: 'minStockLevel',
      sortable: true,
      text: 'Min Stock Level',
      size: '0.4fr'
    },
    {
      accessor: 'maxStockLevel',
      sortable: true,
      text: 'Max Stock Level',
      size: '0.4fr'
    }
  ];

  const [sortColumnId, setSortColumnId] = useState<string>(tableHeaders[0].accessor);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC);

  const processStockBinLevels = (stockBinLevels: StockBinLevel[]) => {
    return stockBinLevels.map(s => ({
      productId: s.product.id,
      deviceName: s.product.name,
      minStockLevel: s.minStockLevel,
      maxStockLevel: s.maxStockLevel,
      recMinStockLevel: s.recMinStockLevel,
      recMaxStockLevel: s.recMaxStockLevel
    }));
  };

  const initialTableData: StockTableData[] = useMemo(() => {
    const data = processStockBinLevels(stockLevels);

    return sort(data, sortColumnId, sortDirection);
  }, [stockLevels]);

  const [editMode, setEditMode] = useState(false);

  const methods = useForm<StockTableFields>({
    resolver: zodResolver(StockLevel),
    defaultValues: { stockData: initialTableData }
  });

  const error = methods.formState.errors.stockData;

  const data = methods.watch('stockData');

  const liveSortedTableData: StockTableData[] = useMemo(() => {
    const sorted = sort(data, sortColumnId, sortDirection);

    methods.setValue('stockData', sorted);

    return sorted;
  }, [sortColumnId, sortDirection]);

  const [loading, setLoading] = useState<boolean>(false);

  const onSave = async () => {
    if (!onSaveCallback) {
      throw Error('Save callback should be defined');
    }

    if (error) {
      toast.error(`Unable to save stock levels: ${error.message}`);
      return;
    }

    setLoading(true);
    try {
      await onSaveCallback(data);
      setEditMode(false);
    } catch (error) {
      toast.error(`Exception - unable to save stock levels ${error}`);
    }

    setLoading(false);
  };

  const onResetStockLevels = () => {
    if (readOnlyTable) {
      throw Error('Should not be attempting to edit stock levels in read only mode');
    }

    methods.setValue('stockData', sort(processStockBinLevels(resetStockLevels!), sortColumnId, sortDirection));
  };

  const status = useStockLevelStatus();

  return (
    <Form className="space-y-3" methods={methods} onSubmit={onSave}>
      <div className="flex justify-between">
        <div className="flex gap-2 items-center">
          <div className="font-bold">Current Stock Level Status:</div>
          <InfoChit type={status === StockLevelStatus.APPROVED ? 'blue' : 'orange'}>
            {status === StockLevelStatus.APPROVED ? 'Approved' : 'Draft'}
          </InfoChit>
        </div>

        {!readOnlyTable && (
          <div className="flex gap-4">
            <Button
              onClick={() => {
                setEditMode(true);
              }}
              variant={ButtonVariant.Secondary}
            >
              <span>Edit</span>
            </Button>
            {resetStockLevels && (
              <Button onClick={onResetStockLevels} variant={ButtonVariant.Mono} disabled={!editMode}>
                <span>Reset to Recommended</span>
              </Button>
            )}
            <Button variant={ButtonVariant.Primary} disabled={!editMode} onClick={onSave} loading={loading}>
              <span>Save</span>
            </Button>
          </div>
        )}
      </div>

      <DaaSTable
        headers={tableHeaders}
        onSortCallback={(columnId, direction) => {
          setSortColumnId(columnId);
          setSortDirection(direction);
        }}
        sortColumnId={sortColumnId}
        sortDirection={sortDirection}
      >
        {liveSortedTableData.map((t, index) => (
          <StockLevelRow data={t} key={t.productId} index={index} editMode={editMode} readonly={readOnlyTable} />
        ))}
      </DaaSTable>
    </Form>
  );
};
